import React from "react";
import { Link, useLocation } from "react-router-dom";
import {
    HiOutlineHome,
    HiOutlineBriefcase,
    HiOutlineChatAlt2,
    HiOutlineCog,
    HiOutlineBell,
    HiOutlineSearch,
    HiOutlinePlus
} from "react-icons/hi";

// Mock Data
const mockBrands = [
    { id: 1, name: "Red Bull", campaigns: 2, logo: "/brands/redbull.png", color: "bg-red-500", shortName: "Red Bull" },
    { id: 2, name: "L'Oréal", campaigns: 22, logo: "/brands/loreal.png", color: "bg-black", shortName: "L'ORÉAL" },
    { id: 3, name: "Adidas", campaigns: 7, logo: "fa-brands fa-adidas", isIcon: true, color: "bg-black", shortName: "adidas" },
    { id: 4, name: "H&M", campaigns: 1, logo: "/brands/hm.png", color: "bg-red-600", shortName: "H&M" },
    { id: 5, name: "Sony", campaigns: 4, logo: "/brands/sony.png", color: "bg-black", shortName: "SONY" },
    { id: 6, name: "Puma", campaigns: 16, logo: "/brands/puma.png", color: "bg-black", shortName: "PUMA" }
];

const CreatorBrands = () => {
    return (
        <div className="flex h-screen bg-[#FDFBF7]">
            {/* Sidebar */}
            <Sidebar />

            {/* Main Content */}
            <main className="flex-1 overflow-hidden flex flex-col h-screen p-4">

                {/* Top Header */}
                <header className="flex justify-between items-center mb-6 shrink-0">
                    <div className="flex-1 max-w-2xl flex gap-4">
                        <div className="relative flex-1">
                            <HiOutlineSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 text-xl" />
                            <input
                                type="text"
                                placeholder="Search"
                                className="w-full bg-white border border-gray-100 rounded-xl py-3 pl-12 pr-4 text-gray-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-100 placeholder-gray-400 font-medium h-12"
                            />
                        </div>
                        <button className="h-12 bg-gray-100 text-gray-700 font-bold px-6 rounded-xl flex items-center gap-2 hover:bg-gray-200 transition-colors shadow-sm shrink-0 text-sm">
                            <HiOutlinePlus className="text-lg" />
                            Add New
                        </button>
                    </div>

                    <div className="flex items-center gap-6 ml-4">
                        <button className="relative text-gray-500 hover:text-gray-700">
                            <HiOutlineBell className="text-2xl" />
                        </button>
                        <div className="flex items-center gap-3">
                            <span className="text-sm font-bold text-gray-800 hidden md:block">Johan Doe</span>
                            <div className="w-10 h-10 rounded-full bg-[#0044CC] flex items-center justify-center text-white font-bold shadow-md overflow-hidden">
                                J
                            </div>
                        </div>
                    </div>
                </header>

                {/* Inner Content Grid */}
                <div className="flex-1 overflow-y-auto pb-6 custom-scrollbar pr-2">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-7xl mx-auto">
                        {mockBrands.map(brand => (
                            <div key={brand.id} className="bg-white rounded-3xl p-6 shadow-sm border border-gray-50 flex flex-col h-[280px]">
                                <div className="flex justify-between items-start mb-6">
                                    <div>
                                        <h3 className="font-bold text-gray-900 text-lg mb-2">{brand.name}</h3>
                                        <div className="bg-[#EBF5FF] text-[#0044CC] text-xs font-bold px-3 py-1 rounded-full inline-block">
                                            {brand.campaigns} {brand.campaigns === 1 ? 'Campaign' : 'Campaigns'}
                                        </div>
                                    </div>
                                    <button className="w-8 h-8 rounded-full bg-gray-50 flex items-center justify-center text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors">
                                        <i className="fa-solid fa-ellipsis"></i>
                                    </button>
                                </div>

                                <div className="flex-1 flex items-center justify-center">
                                    {brand.isIcon ? (
                                        <div className="flex flex-col items-center gap-1">
                                            <i className={`${brand.logo} text-5xl`}></i>
                                            <span className="font-bold text-xl tracking-tighter">{brand.shortName}</span>
                                        </div>
                                    ) : (
                                        <h2 className={`font-black tracking-tighter italic text-4xl ${brand.name === 'Red Bull' ? 'text-red-500' : 'text-gray-800'}`}>
                                            {brand.shortName}
                                        </h2>
                                    )}
                                </div>

                                <Link 
                                    to={`/creator/brands/${brand.id}`}
                                    className="w-full bg-black text-white py-3.5 rounded-xl font-bold text-sm text-center hover:bg-gray-800 transition-colors mt-6 block"
                                >
                                    View
                                </Link>
                            </div>
                        ))}
                    </div>
                </div>

            </main>
        </div>
    );
};

// Extracted Sidebar to reuse identically
const Sidebar = () => {
    const location = useLocation();
    const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

    return (
        <aside className="w-64 bg-white border-r border-gray-100 flex flex-col hidden md:flex shrink-0 z-20">
            <div className="p-6 flex items-center gap-2 text-black font-bold text-xl">
                <img src="/logo.png" alt="Braccet" className="w-8 h-8 object-contain" />
                <span>Braccet</span>
            </div>

            <nav className="flex-1 px-4 py-6 space-y-2">
                <SidebarItem icon={<HiOutlineHome />} label="Dashboard" to="/creator/dashboard" active={location.pathname === '/creator/dashboard'} />
                <SidebarItem icon={<HiOutlineBriefcase />} label="Campaigns" to="/creator/find-work" />
                <SidebarItem icon={<span className="font-bold text-lg">(B)</span>} label="Brands" to="/creator/brands" active={isActive('/creator/brands')} />
                <SidebarItem icon={<HiOutlineChatAlt2 />} label="Messages" to="/creator/messages" />
                <SidebarItem icon={<HiOutlineCog />} label="Settings" to="/creator/profile" />
            </nav>

            <div className="p-4 border-t border-gray-100">
                <button className="flex w-full items-center gap-3 p-2 rounded-xl text-gray-500 font-bold hover:bg-gray-50 hover:text-red-500 transition-colors">
                    <svg className="w-6 h-6 rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" /></svg>
                    Logout
                </button>
            </div>
        </aside>
    );
};

const SidebarItem = ({ icon, label, to, active }) => (
    <Link to={to} className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors ${active ? 'bg-blue-50 text-[#0044CC]' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'}`}>
        <span className="text-xl">{icon}</span>
        {label}
    </Link>
);

export default CreatorBrands;
